import { SHOWCASE_IMAGES } from "./showcase-images.js?v=20261007m";
import { fillIcons } from "./fill-icons.js?v=20261007m";
import { seasonalSprinkleShapes, sprinklePieceSizePx, MAPLE_LEAF_PATH, SPARKLE_PATH, SPRINKLE_SHAPE_VER } from "./sprinkle-rain.js?v=20261007m";
import { onLayoutWidthChange } from "./layout-resize.js?v=20261007m";

const REDUCE_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)");
const PEEK_ALIASES = {
  peek: "scenes",
  look: "scenes",
  how: "credits",
  tiers: "membership",
};

function encodedPath(path) {
  return path.split("/").map(encodeURIComponent).join("/");
}

function initPeekTabs() {
  const peek = document.getElementById("peek");
  const tabs = [...document.querySelectorAll(".peek__tab[data-peek]")];
  const panels = {
    scenes: document.getElementById("peek-scenes"),
    credits: document.getElementById("peek-credits"),
    membership: document.getElementById("peek-membership"),
  };
  if (!peek || !tabs.length) return;

  try {
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";
  } catch (_) {
    /* ignore */
  }

  const scrollToCover = (smooth) => {
    const cover = document.getElementById("top");
    const behavior = smooth && !REDUCE_MOTION.matches ? "smooth" : "auto";
    if (cover) {
      cover.scrollIntoView({ block: "start", behavior });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior });
    }
  };

  const scrollToPeek = (smooth) => {
    peek.scrollIntoView({
      block: "start",
      behavior: smooth && !REDUCE_MOTION.matches ? "smooth" : "auto",
    });
  };

  const showPeek = (requested, updateHash = false) => {
    const name = panels[requested] ? requested : PEEK_ALIASES[requested] || "scenes";
    tabs.forEach((tab) => {
      const active = tab.dataset.peek === name;
      tab.classList.toggle("is-on", active);
      tab.setAttribute("aria-selected", String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    Object.entries(panels).forEach(([key, panel]) => {
      if (panel) panel.hidden = key !== name;
    });
    if (updateHash) {
      const url = new URL(window.location.href);
      url.hash = `peek=${name}`;
      history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    }
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener("click", () => showPeek(tab.dataset.peek, true));
    tab.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      const step = event.key === "ArrowRight" ? 1 : -1;
      const next = tabs[(index + step + tabs.length) % tabs.length];
      showPeek(next.dataset.peek, true);
      next.focus();
    });
  });

  document.querySelectorAll('a[href="#peek"], a[href^="#peek="]').forEach((link) => {
    link.addEventListener("click", (event) => {
      event.preventDefault();
      const href = link.getAttribute("href") || "#peek";
      showPeek(href.startsWith("#peek=") ? href.slice(6) : "scenes", true);
      scrollToPeek(true);
    });
  });

  const rawHash = window.location.hash.replace(/^#/, "");
  const stayOnCover = !rawHash || rawHash === "top" || rawHash === "join";
  if (rawHash.startsWith("peek=")) {
    showPeek(rawHash.slice(5));
    requestAnimationFrame(() => scrollToPeek(false));
  } else if (PEEK_ALIASES[rawHash]) {
    showPeek(PEEK_ALIASES[rawHash]);
    requestAnimationFrame(() => scrollToPeek(false));
  } else if (stayOnCover) {
    // Refresh / #top / #join must not restore a previous peek scroll position.
    const pinTop = () => scrollToCover(false);
    pinTop();
    requestAnimationFrame(pinTop);
    window.addEventListener("load", pinTop, { once: true });
  }

  let snapLocked = false;
  const cover = document.getElementById("top");
  const maybeSnap = (deltaY) => {
    if (REDUCE_MOTION.matches || snapLocked || deltaY <= 0 || !cover) return;
    const peekTop = peek.getBoundingClientRect().top;
    const coverBottom = cover.getBoundingClientRect().bottom;
    if ((peekTop > -8 && peekTop < 48) || coverBottom > window.innerHeight * 0.92 || coverBottom < 40) return;
    snapLocked = true;
    scrollToPeek(true);
    window.setTimeout(() => {
      snapLocked = false;
    }, 700);
  };

  window.addEventListener("wheel", (event) => maybeSnap(event.deltaY), { passive: true });
  let touchY = null;
  window.addEventListener("touchstart", (event) => {
    touchY = event.touches[0]?.clientY ?? null;
  }, { passive: true });
  window.addEventListener("touchend", (event) => {
    if (touchY == null || !event.changedTouches[0]) return;
    const deltaY = touchY - event.changedTouches[0].clientY;
    touchY = null;
    maybeSnap(deltaY);
  }, { passive: true });

  const backLogin = document.getElementById("peek-back-login");
  backLogin?.addEventListener("click", (event) => {
    event.preventDefault();
    if (!cover) return;
    const url = new URL(window.location.href);
    url.hash = "top";
    history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    scrollToCover(true);
  });
  fillIcons(document.getElementById("peek-sticky") || document);
}

function initShowcasePager() {
  const grid = document.getElementById("look-grid");
  const row = document.getElementById("look-pager-row");
  const pager = document.getElementById("look-pager");
  const dots = document.getElementById("look-dots");
  const previous = document.getElementById("look-prev");
  const next = document.getElementById("look-next");
  if (!grid) return;

  let page = 0;
  let lastPageSize = 0;
  const pageSize = () => (window.innerWidth <= 800 ? 4 : 6);
  const pageCount = () => Math.max(1, Math.ceil(SHOWCASE_IMAGES.length / pageSize()));

  const render = () => {
    const size = pageSize();
    if (lastPageSize && lastPageSize !== size) page = 0;
    lastPageSize = size;
    const pages = pageCount();
    page = Math.max(0, Math.min(page, pages - 1));
    grid.innerHTML = SHOWCASE_IMAGES.slice(page * size, page * size + size)
      .map(
        (src) =>
          `<figure data-save-guard-root><img src="${encodedPath(src)}" alt="" width="640" height="480" loading="lazy" decoding="async" draggable="false" data-save-guard /></figure>`
      )
      .join("");

    const showPager = pages > 1;
    if (row) row.classList.toggle("look__pager-row--paged", showPager);
    if (previous) {
      previous.hidden = !showPager;
      previous.disabled = page === 0;
    }
    if (next) {
      next.hidden = !showPager;
      next.disabled = page >= pages - 1;
    }

    if (!pager || !dots) return;
    pager.hidden = !showPager;
    dots.replaceChildren();
    for (let index = 0; index < pages; index += 1) {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = `look__dot${index === page ? " is-on" : ""}`;
      dot.setAttribute("aria-label", `Showcase page ${index + 1}`);
      if (index === page) dot.setAttribute("aria-current", "true");
      else dot.removeAttribute("aria-current");
      dot.addEventListener("click", () => {
        page = index;
        render();
      });
      dots.appendChild(dot);
    }
    if (row) fillIcons(row);
  };

  previous?.addEventListener("click", () => {
    page -= 1;
    render();
  });
  next?.addEventListener("click", () => {
    page += 1;
    render();
  });

  onLayoutWidthChange(render);
  render();
}

function sprinkleSvg(type, color) {
  if (type === "heart") return `<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="${color}" d="M12 20s-7-4.5-9.5-9A4.5 4.5 0 0 1 12 6a4.5 4.5 0 0 1 9.5 5c-2.5 4.5-9.5 9-9.5 9Z"/></svg>`;
  if (type === "snowflake") return `<svg viewBox="0 0 24 24" aria-hidden="true"><g fill="none" stroke="${color}" stroke-width="1.6" stroke-linecap="round"><path d="M12 3v18M5 7.5l14 9M5 16.5l14-9"/></g></svg>`;
  if (type === "leaf") return `<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="${color}" d="${MAPLE_LEAF_PATH}"/></svg>`;
  if (type === "flower") return `<svg viewBox="0 0 24 24" aria-hidden="true"><g fill="${color}"><circle cx="12" cy="7.2" r="2.4"/><circle cx="16.5" cy="10.2" r="2.4"/><circle cx="15.2" cy="15.2" r="2.4"/><circle cx="8.8" cy="15.2" r="2.4"/><circle cx="7.5" cy="10.2" r="2.4"/><circle cx="12" cy="12" r="2.1"/></g></svg>`;
  if (type === "sparkle" || type === "star") {
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="${color}" d="${SPARKLE_PATH}"/></svg>`;
  }
  return `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="5" fill="${color}"/></svg>`;
}

function initHomeSprinkles() {
  const container = document.getElementById("sprinkle-rain");
  if (!container || REDUCE_MOTION.matches) return;
  if (
    container.dataset.wired === "1" &&
    container.dataset.shapeVer === SPRINKLE_SHAPE_VER
  ) {
    return;
  }
  container.innerHTML = "";
  container.dataset.wired = "1";
  container.dataset.shapeVer = SPRINKLE_SHAPE_VER;
  const shapes = seasonalSprinkleShapes();
  const colors = [
    "var(--accent-soft)",
    "var(--secondary-soft)",
    "var(--accent)",
    "var(--secondary)",
    "var(--accent-deep)",
    "var(--secondary-deep)",
  ];
  const count = window.innerWidth <= 720 ? 22 : 40;

  for (let index = 0; index < count; index += 1) {
    const piece = document.createElement("span");
    const type = shapes[index % shapes.length];
    piece.className = "sprinkle-rain__piece";
    piece.innerHTML = sprinkleSvg(type, colors[index % colors.length]);
    const band = (index + 0.5) / count;
    const jitter = (Math.random() - 0.5) * (0.85 / count);
    piece.style.left = `${Math.min(97, Math.max(2, (band + jitter) * 100))}%`;
    piece.style.animationDuration = `${18 + Math.random() * 22}s`;
    piece.style.animationDelay = `${Math.random() * -28}s`;
    piece.style.setProperty("--drift", `${-30 + Math.random() * 60}px`);
    piece.style.setProperty("--spin", `${Math.random() * 360}deg`);
    piece.style.setProperty("--size", `${sprinklePieceSizePx(type)}px`);
    container.appendChild(piece);
  }

  const syncFallDistance = () => {
    container.style.setProperty("--fall", `${Math.max(container.offsetHeight, window.innerHeight) + 40}px`);
  };
  const syncPlayback = () => {
    container.querySelectorAll(".sprinkle-rain__piece").forEach((piece) => {
      piece.style.animationPlayState = document.hidden ? "paused" : "running";
    });
  };
  syncFallDistance();
  syncPlayback();
  window.addEventListener("resize", syncFallDistance);
  window.addEventListener("load", syncFallDistance, { once: true });
  document.addEventListener("visibilitychange", syncPlayback);
}

function initCoverHandoff() {
  const slides = [...document.querySelectorAll("#cover-slides img")];
  if (slides.length < 2) return;
  const holdMs = 5200;
  const fadeMs = 1400;
  const minScale = 1.06;
  const maxScale = 1.18;
  let index = 0;
  // First slide: appear at min, zoom in. Next: appear at max, zoom out. Then in, out, ...
  let nextZoomsOut = true;
  let timer;

  slides.forEach((slide, slideIndex) => {
    slide.style.opacity = slideIndex === 0 ? "1" : "0";
    slide.style.transform = `scale(${minScale})`;
    slide.style.transition = "none";
    slide.classList.toggle("is-active", slideIndex === 0);
  });

  const forceReflow = (el) => {
    void el.offsetWidth;
  };

  const schedule = (delay) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(advance, delay);
  };

  const advance = () => {
    if (document.hidden) {
      schedule(holdMs);
      return;
    }
    const current = slides[index];
    const nextIndex = (index + 1) % slides.length;
    const next = slides[nextIndex];
    const handoff = nextZoomsOut ? maxScale : minScale;
    const target = nextZoomsOut ? minScale : maxScale;

    current.classList.add("is-leaving");
    current.style.transition = `opacity ${fadeMs}ms ease`;
    current.style.transform = `scale(${handoff})`;

    next.style.transition = "none";
    next.style.opacity = "0";
    next.style.transform = `scale(${handoff})`;
    next.classList.add("is-active");
    forceReflow(next);

    requestAnimationFrame(() => {
      next.style.transition = REDUCE_MOTION.matches
        ? `opacity ${fadeMs}ms ease`
        : `opacity ${fadeMs}ms ease, transform ${holdMs}ms linear`;
      current.style.opacity = "0";
      next.style.opacity = "1";
      if (!REDUCE_MOTION.matches) next.style.transform = `scale(${target})`;
    });

    window.setTimeout(() => {
      current.classList.remove("is-active", "is-leaving");
      current.style.transition = "none";
      current.style.opacity = "0";
      current.style.transform = `scale(${minScale})`;
      index = nextIndex;
      nextZoomsOut = !nextZoomsOut;
    }, fadeMs + 40);

    // Next swap when this slide's zoom finishes (same clock as the transform).
    schedule(holdMs);
  };

  if (!REDUCE_MOTION.matches) {
    requestAnimationFrame(() => {
      forceReflow(slides[0]);
      slides[0].style.transition = `transform ${holdMs}ms linear`;
      slides[0].style.transform = `scale(${maxScale})`;
    });
  }
  schedule(holdMs);
}

export function initHomeLandingUi() {
  if (!document.body.classList.contains("landing--cover")) return;
  initPeekTabs();
  initShowcasePager();
  initHomeSprinkles();
  initCoverHandoff();
}
